import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Party } from '../api';

// Resolves a party id to its name, once per signed-in session. A failed fetch is
// not kept, so the next render asks again.
const cache = new Map<string, Promise<Party | null>>();
export function fetchParty(id: string): Promise<Party | null> {
  if (!cache.has(id)) {
    const p: Promise<Party | null> = api<Party>(`/v1/parties/${id}`).catch(() => { if (cache.get(id) === p) cache.delete(id); return null; });
    cache.set(id, p);
  }
  return cache.get(id)!;
}
export const forgetParty = (id: string) => cache.delete(id);
// Names one person may see are not another's: App clears them when who is signed in changes.
export const clearParties = () => cache.clear();

export function PartyName({ id, link = true }: { id: string; link?: boolean }) {
  const [p, setP] = useState<Party | null | undefined>(undefined);
  useEffect(() => { let live = true; fetchParty(id).then((x) => live && setP(x)); return () => { live = false; }; }, [id]);
  if (p === undefined) return <span aria-busy="true">…<span className="sr-only">Loading name</span></span>;
  const name = p ? p.display_name : 'Unknown contact';
  return link && p ? <Link to={`/contacts/${id}`}>{name}</Link> : <span>{name}</span>;
}

// The name the API gives a party you may not see on a record you may. It has
// no page you can open, so it is plain text, never a link.
export const HIDDEN_PARTY = 'Hidden contact';
export const isHiddenParty = (name: string | null | undefined) => name === HIDDEN_PARTY;

export function PartyLink({ id, name, className }: { id: string | null; name: string | null; className?: string }) {
  if (!id || isHiddenParty(name)) return <span className={['muted', className].filter(Boolean).join(' ')}>{name ?? HIDDEN_PARTY}</span>;
  return <Link className={className} to={`/contacts/${id}`}>{name}</Link>;
}
